import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactCountryFlag from "react-country-flag";
import {
    FaCoins
} from "react-icons/fa";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import SiteHeader from "./SiteHeader";

const currencyMap = {
    USD: { country: "US", symbol: <span className="inline-block ml-1">$</span>},
    EUR: { country: "EU", symbol: <span className="inline-block ml-1">€</span>},
    JPY: { country: "JP", symbol: <span className="inline-block ml-1">¥</span>},
    GBP: { country: "GB", symbol: <span className="inline-block ml-1">£</span>},
    AUD: { country: "AU", symbol: <span className="inline-block ml-1">$</span>},
    CAD: { country: "CA", symbol: <span className="inline-block ml-1">$</span>},
    CHF: { country: "CH", symbol: <span className="inline-block ml-1">₣</span>},
    SEK: { country: "SE", symbol: <span className="inline-block ml-1">kr</span>},
    NOK: { country: "NO", symbol: <span className="inline-block ml-1">kr</span>},
    NZD: { country: "NZ", symbol: <span className="inline-block ml-1">$</span>},
    KRW: { country: "KR", symbol: <span className="inline-block ml-1">₩</span>},
};

const metalIcons = {
    USDXAU: <FaCoins className="inline-block text-yellow-400 text-xl ml-2" />,
    USDXAG: <FaCoins className="inline-block text-gray-300 text-xl ml-2" />,
    USDXPT: <FaCoins className="inline-block text-gray-400 text-xl ml-2" />,
};

function LivePricesDashboard() {
    const [latest, setLatest] = useState(null);
    const [history, setHistory] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState("USD_CAD");
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem("dominion-dark-mode");
        return saved ? JSON.parse(saved) : false;
    });

    const API_BASE = "https://dominion-financial.ca/php";

    // --- Handle dark mode toggle and persistence ---
    const handleToggleTheme = () => {
        setIsDarkMode((prev) => {
            const newValue = !prev;
            localStorage.setItem("dominion-dark-mode", JSON.stringify(newValue));
            return newValue;
        });
    };

    // --- Load latest prices & initial chart ---
    useEffect(() => {
        fetch(`${API_BASE}/latest_prices.php`)
            .then((res) => res.json())
            .then((data) => {
                setLatest(data);
                fetchHistory("USD_CAD"); // default chart
            })
            .catch((err) => console.error("Failed to fetch latest prices:", err));
    }, []);

    // --- Initialize unified.js dashboard logic ---
    useEffect(() => {
        if (window.initializeDashboard) {
            try {
                window.initializeDashboard("react");
            } catch (err) {
                console.error("initializeDashboard() error:", err);
            }
        } else {
            console.warn("initializeDashboard() not found on window.");
        }
    }, []);

    const fetchHistory = async (symbol) => {
        try {
            let querySymbol = symbol;
            if (symbol === "USD" || symbol === "CAD") querySymbol = "USD_CAD";

            const res = await fetch(`${API_BASE}/price_history.php?symbol=${querySymbol}`);
            const data = await res.json();

            let transformed = data.map((d) => {
                const priceNum = Number(d.price);
                const priceDisplay = priceNum.toFixed(4);
                return {
                    date: new Date(d.retrieved_at).toLocaleString("en-CA", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    price: priceNum,
                    price_display: priceDisplay,
                };
            });

            if (querySymbol.startsWith("USD_") && querySymbol !== "USD_CAD" && !["USDXAU", "USDXAG", "USDXPT"].includes(querySymbol)) {
                transformed = transformed.map((d) => ({
                    ...d,
                    price: 1 / d.price,
                    price_display: (1 / d.price).toFixed(4),
                }));
            }
            if (symbol === "CAD") {
                transformed = transformed.map((d) => ({
                    ...d,
                    price: 1 / d.price,
                    price_display: (1 / d.price).toFixed(4),
                }));
            }

            setHistory(transformed);
            setSelectedSymbol(symbol);
        } catch (err) {
            console.error("History fetch failed:", err);
        }
    };

    const currencyOrder = ['USD', 'CAD', 'EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'JPY', 'KRW', 'SEK', 'NOK'];
    const metalOrder = ['USDXAU', 'USDXAG', 'USDXPT'];

    const renderCurrencyCards = () => {
        if (!latest || !latest.currencies) return null;
        const cards = [];
        const usdCad = latest.currencies["USD_CAD"];
        if (usdCad && usdCad.price) {
            const usdToCad = Number(usdCad.price).toFixed(4);
            const cadToUsd = (1 / Number(usdCad.price)).toFixed(4);
            const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";
            const textColor = isDarkMode ? "text-gray-100" : "text-[#004080]";
            cards.push({
                code: "USD",
                element: (
                    <div key="USD" onClick={() => fetchHistory("USD_CAD")} className={`p-4 shadow rounded ${cardBg} flex justify-between items-center hover:scale-105 transition-transform cursor-pointer`}>
                        <div className="flex items-center space-x-2">
                            <ReactCountryFlag countryCode="US" svg style={{ width: "1.5em", height: "1.5em" }} />
                            <h3 className={`font-semibold ${textColor}`}>USD $</h3>
                        </div>
                        <p className={`text-lg font-bold ${textColor}`}>{usdToCad} CAD</p>
                    </div>
                ),
            });
            cards.push({
                code: "CAD",
                element: (
                    <div key="CAD" onClick={() => fetchHistory("USD_CAD")} className={`p-4 shadow rounded ${cardBg} flex justify-between items-center hover:scale-105 transition-transform cursor-pointer`}>
                        <div className="flex items-center space-x-2">
                            <ReactCountryFlag countryCode="CA" svg style={{ width: "1.5em", height: "1.5em" }} />
                            <h3 className={`font-semibold ${textColor}`}>CAD $</h3>
                        </div>
                        <p className={`text-lg font-bold ${textColor}`}>{cadToUsd} USD</p>
                    </div>
                ),
            });
        }

        Object.keys(latest.currencies).forEach((pair) => {
            if (pair === "USD_CAD" || pair.startsWith("USDX")) return;
            const cur = latest.currencies[pair];
            if (!cur) return;
            const parts = pair.split("_");
            const base = parts[1] || parts[0];
            if (base === "USD" || base === "CAD") return;
            const map = currencyMap[base] || {};
            const invertedPrice = (1 / Number(cur.price)).toFixed(4);
            const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";
            const textColor = isDarkMode ? "text-gray-100" : "text-[#004080]";
            cards.push({
                code: base,
                element: (
                    <div key={pair} onClick={() => fetchHistory(pair)} className={`p-4 shadow rounded ${cardBg} flex justify-between items-center hover:scale-105 transition-transform cursor-pointer`}>
                        <div className="flex items-center space-x-2">
                            {map.country && <ReactCountryFlag countryCode={map.country} svg style={{ width: "1.5em", height: "1.5em" }} />}
                            <h3 className={`font-semibold ${textColor}`}>{base} {map.symbol}</h3>
                        </div>
                        <p className={`text-lg font-bold ${textColor}`}>{invertedPrice} USD</p>
                    </div>
                ),
            });
        });
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {currencyOrder.map((c) => cards.find((x) => x.code === c)).filter(Boolean).map((c) => c.element)}
            </div>
        );
    };

    const renderMetalCards = () => {
        if (!latest || !latest.metals) return null;
        const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";
        const textColor = isDarkMode ? "text-gray-100" : "text-[#004080]";
        const cards = Object.keys(latest.metals).map((symbol) => {
            const metal = latest.metals[symbol];
            const icon = metalIcons[symbol];
            const formattedPrice = Number(metal.price).toFixed(2);
            return {
                code: symbol,
                element: (
                    <div key={symbol} onClick={() => fetchHistory(symbol)} className={`p-4 shadow rounded ${cardBg} flex justify-between items-center hover:scale-105 transition-transform cursor-pointer`}>
                        <div className="flex items-center space-x-2">
                            {icon}
                            <h3 className={`font-semibold ${textColor}`}>{symbol}</h3>
                        </div>
                        <p className={`text-lg font-bold ${textColor}`}>{formattedPrice} {metal.quote}</p>
                    </div>
                ),
            };
        });
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {metalOrder.map((m) => cards.find((x) => x.code === m)).filter(Boolean).map((x) => x.element)}
            </div>
        );
    };

    const renderChart = () => {
        if (!selectedSymbol || history.length === 0) return null;

        // Metals = 2 decimals, currencies = 4
        const isMetal = ["USDXAU", "USDXAG", "USDXPT"].includes(selectedSymbol);
        const decimalPlaces = isMetal ? 2 : 4;
        const chartBg = isDarkMode ? "#1a1a1a" : "#ffffff";
        const textColor = isDarkMode ? "#e0e0e0" : "#004080";
        // Line Color: Deep blue for light mode, Gold/Yellow for dark mode
        const lineColor = isDarkMode ? "#ffffff" : "#004080";

        return (
            <div className={`mt-8 p-4 rounded shadow ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                <h3 className={`font-semibold mb-2 ${isDarkMode ? "text-gray-100" : "text-[#004080]"}`}>{selectedSymbol} Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={history} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: textColor }} />
                        <YAxis
                            domain={["auto", "auto"]}
                            tickFormatter={(value) => value.toFixed(decimalPlaces)}
                            tick={{ fontSize: 12, fill: textColor }}
                        />
                        <Tooltip formatter={(value) => value.toFixed(decimalPlaces)} contentStyle={{ backgroundColor: chartBg, border: `1px solid ${textColor}`, color: textColor }} />
                        <Line type="monotone" dataKey="price" stroke={lineColor} strokeWidth={1} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className={isDarkMode ? "bg-gray-900 text-gray-100" : "bg-[#eef4ff] text-gray-900"} style={{ minHeight: "100vh" }}>
            {/* Set favicon */}
            <Helmet>
                <link rel="icon" href="/favicon.ico" />
            </Helmet>

            {/* Site Header */}
            <SiteHeader isDarkMode={isDarkMode} onToggleTheme={handleToggleTheme} />

            {/* Main */}
            <main className="max-w-6xl mx-auto py-10 px-0 space-y-12">
                <section>
                    <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? "text-gray-100" : "text-[#004080]"}`}>Foreign Exchange</h2>
                    {renderCurrencyCards()}
                </section>

                <section>
                    <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? "text-gray-100" : "text-[#004080]"}`}>Precious Metals</h2>
                    {renderMetalCards()}
                </section>

                {renderChart()}
            </main>

            <footer className={isDarkMode ? "bg-gray-800 text-gray-100" : "bg-[#00264d] text-white"}>
                <div className="text-center py-6">
                    <p className="text-sm">
                        © {new Date().getFullYear()} dominion-financial.ca All Rights Reserved.
                    </p>
                    <p className="text-sm mt-2">207 W Hastings St, Vancouver, BC V6B 1H7 | info@dominion-financial.ca</p>
                </div>
            </footer>

        </div>
    );
}

export default LivePricesDashboard;
